import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { formatDateToLocal } from '../utils/date';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { accountGeneration, accountStorage, scopeToAccount } from '../lib/accountStorage';

/**
 * ⚠️ `free` is a self-reported stretch of time logged in Free mode — the day as
 * it actually went, rather than an item the app watched you complete. It always
 * carries `xpEarned: 0` and `metadata.source = 'free-mode'`.
 *
 * ⚠️⚠️ THE LOG IS THIS DEVICE'S, ONE COPY PER ACCOUNT (lib/accountStorage). Nothing calls
 * `initialize()` — the rows written to `activity_logs` are never read back — so the saved
 * copy IS the history the journey signals, reveals and charts read. It was one key for the
 * device: the next account to sign in here was judged on the last one's activity.
 */
export type ActivityType = 'habit' | 'task' | 'project' | 'quest' | 'skill' | 'breathwork' | 'level_up' | 'event' | 'program_module' | 'program_completion' | 'free';

export interface ActivityLog {
  id: string;
  userId: string;
  activityType: ActivityType;
  activityName: string;
  domainId: string;
  xpEarned: number;
  metadata?: {
    questName?: string;
    projectName?: string;
    abilityName?: string;
    styleName?: string;
    levelType?: 'user' | 'domain';
    oldLevel?: number;
    newLevel?: number;
    [key: string]: any;
  };
  completedAt: string;
  createdAt: string;
}

export interface DailyXPSummary {
  date: string;
  totalXP: number;
  activities: ActivityLog[];
}

export interface XPStats {
  totalXP: number;
  topXPDay: string;
  topXPDayTotal: number;
  bestSingleDay: number;
  bestSingleDayDate: string;
  averageDailyXP: number;
}

interface ActivityLogState {
  logs: ActivityLog[];
  isInitialized: boolean;
  isSyncing: boolean;

  initialize: () => Promise<void>;
  addActivity: (activity: Omit<ActivityLog, 'id' | 'userId' | 'createdAt'>) => Promise<void>;

  getActivitiesByDateRange: (startDate: string, endDate: string) => ActivityLog[];

  getActivitiesByType: (type: ActivityType) => ActivityLog[];

  getActivitiesByDomain: (domainId: string) => ActivityLog[];

  getDailyXPTotals: (days: number) => DailyXPSummary[];

  getXPStats: (days: number) => XPStats;

  deleteActivitiesOlderThan: (days: number) => void;

  clearAllActivities: () => void;
}

export const useActivityLogStore = create<ActivityLogState>()(
  persist(
    (set, get) => ({
      logs: [],
      isInitialized: false,
      isSyncing: false,

      initialize: async () => {
        if (get().isInitialized) return;

        try {
          const { user } = await syncManager.safeAuthCheck();

          if (user) {
            const { data, error } = await supabase
              .from('activity_logs')
              .select('*')
              .order('completed_at', { ascending: false })
              .limit(500);

            if (error) throw error;

            if (data) {
              const formattedLogs: ActivityLog[] = data.map(log => ({
                id: log.id,
                userId: log.user_id,
                activityType: log.activity_type as ActivityType,
                activityName: log.activity_name,
                domainId: log.domain_id,
                xpEarned: log.xp_earned,
                metadata: log.metadata || {},
                completedAt: log.completed_at,
                createdAt: log.created_at
              }));

              set({ logs: formattedLogs, isInitialized: true });
            }
          } else {
            set({ isInitialized: true });
          }
        } catch (error) {
          console.error('Failed to initialize activity logs:', error);
          set({ isInitialized: true });
        }
      },

      addActivity: async (activity) => {
        const at = accountGeneration();
        const newActivity: ActivityLog = {
          ...activity,
          id: crypto.randomUUID(),
          userId: 'local-user',
          createdAt: new Date().toISOString()
        };

        set((state) => ({
          logs: [...state.logs, newActivity]
        }));

        try {
          const { user } = await syncManager.safeAuthCheck();
          // ⚠️⚠️ Asked as the account signs out, the check answers with the NEXT account —
          // which would get this row.
          if (at !== accountGeneration()) return;

          if (user) {
            set({ isSyncing: true });

            const { error } = await supabase
              .from('activity_logs')
              .insert({
                id: newActivity.id,
                user_id: user.id,
                activity_type: newActivity.activityType,
                activity_name: newActivity.activityName,
                domain_id: newActivity.domainId,
                xp_earned: newActivity.xpEarned,
                metadata: newActivity.metadata || {},
                completed_at: newActivity.completedAt,
                created_at: newActivity.createdAt
              });

            if (error) {
              console.error('Failed to sync activity log to Supabase:', error);
            }

            set({ isSyncing: false });
          }
        } catch (error) {
          console.error('Error syncing activity log:', error);
          set({ isSyncing: false });
        }
      },

      getActivitiesByDateRange: (startDate, endDate) => {
        const start = new Date(startDate).getTime();
        const end = new Date(endDate).getTime();

        return get().logs.filter(log => {
          const logDate = new Date(log.completedAt).getTime();
          return logDate >= start && logDate <= end;
        }).sort((a, b) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime());
      },

      getActivitiesByType: (type) => {
        return get().logs.filter(log => log.activityType === type)
          .sort((a, b) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime());
      },

      getActivitiesByDomain: (domainId) => {
        return get().logs.filter(log => log.domainId === domainId)
          .sort((a, b) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime());
      },

      getDailyXPTotals: (days) => {
        const now = new Date();
        const startDate = new Date(now);
        startDate.setDate(now.getDate() - days + 1);
        startDate.setHours(0, 0, 0, 0);

        const logs = get().logs.filter(log => {
          const logDate = new Date(log.completedAt);
          return logDate >= startDate;
        });

        const dailyMap = new Map<string, DailyXPSummary>();

        for (let i = 0; i < days; i++) {
          const date = new Date(startDate);
          date.setDate(startDate.getDate() + i);
          const dateStr = formatDateToLocal(date);

          dailyMap.set(dateStr, {
            date: dateStr,
            totalXP: 0,
            activities: []
          });
        }

        logs.forEach(log => {
          const dateStr = formatDateToLocal(new Date(log.completedAt));
          const existing = dailyMap.get(dateStr);

          if (existing) {
            existing.totalXP += log.xpEarned;
            existing.activities.push(log);
          }
        });

        return Array.from(dailyMap.values());
      },

      getXPStats: (days) => {
        const dailyTotals = get().getDailyXPTotals(days);

        const totalXP = dailyTotals.reduce((sum, day) => sum + day.totalXP, 0);

        const dayOfWeekTotals: { [key: string]: number } = {
          Sunday: 0,
          Monday: 0,
          Tuesday: 0,
          Wednesday: 0,
          Thursday: 0,
          Friday: 0,
          Saturday: 0
        };

        const dayOfWeekCounts: { [key: string]: number } = {
          Sunday: 0,
          Monday: 0,
          Tuesday: 0,
          Wednesday: 0,
          Thursday: 0,
          Friday: 0,
          Saturday: 0
        };

        dailyTotals.forEach(day => {
          const date = new Date(day.date);
          const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
          dayOfWeekTotals[dayName] = (dayOfWeekTotals[dayName] || 0) + day.totalXP;
          dayOfWeekCounts[dayName] = (dayOfWeekCounts[dayName] || 0) + 1;
        });

        let topXPDay = 'Monday';
        let topXPDayAvg = 0;

        Object.keys(dayOfWeekTotals).forEach(day => {
          const count = dayOfWeekCounts[day] || 1;
          const avg = dayOfWeekTotals[day] / count;
          if (avg > topXPDayAvg) {
            topXPDayAvg = avg;
            topXPDay = day;
          }
        });

        let bestSingleDay = 0;
        let bestSingleDayDate = '';

        dailyTotals.forEach(day => {
          if (day.totalXP > bestSingleDay) {
            bestSingleDay = day.totalXP;
            bestSingleDayDate = day.date;
          }
        });

        const daysWithActivity = dailyTotals.filter(day => day.totalXP > 0).length;
        const averageDailyXP = daysWithActivity > 0 ? totalXP / daysWithActivity : 0;

        return {
          totalXP,
          topXPDay,
          topXPDayTotal: Math.round(topXPDayAvg),
          bestSingleDay,
          bestSingleDayDate,
          averageDailyXP: Math.round(averageDailyXP)
        };
      },

      deleteActivitiesOlderThan: (days) => {
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - days);
        cutoffDate.setHours(0, 0, 0, 0);

        set((state) => ({
          logs: state.logs.filter(log => {
            const logDate = new Date(log.completedAt);
            return logDate >= cutoffDate;
          })
        }));
      },

      clearAllActivities: () => {
        set({ logs: [] });
      }
    }),
    {
      name: 'activity-log-storage',
      version: 1,
      storage: accountStorage('claim'),
    }
  )
);

scopeToAccount(useActivityLogStore);
